import { useCallback, useRef, useState } from 'react';
import { schemaToFormData } from './questionnaire-schema';
import {
  createDefaultSettings,
  createEmptyForm,
  type Question,
  type QuestionnaireFormData,
  type QuestionnaireSchema,
  type Section,
} from './types';

function newClientSectionId() {
  return `s-${globalThis.crypto?.randomUUID?.() ?? String(Date.now())}`;
}

function newClientQuestionId() {
  return `q-${globalThis.crypto?.randomUUID?.() ?? String(Date.now())}`;
}

function cloneFormBaseline(fd: QuestionnaireFormData): QuestionnaireFormData {
  if (typeof structuredClone === 'function') {
    return structuredClone(fd);
  }
  return JSON.parse(JSON.stringify(fd)) as QuestionnaireFormData;
}

export function useQuestionnaireFormState() {
  const [form, setForm] = useState<QuestionnaireFormData>(createEmptyForm);
  const structureBaselineRef = useRef<QuestionnaireFormData | null>(null);

  const setTitle = useCallback((title: string) => setForm((f) => ({ ...f, title })), []);
  const setDescription = useCallback((description: string) => setForm((f) => ({ ...f, description })), []);
  const setType = useCallback((type: QuestionnaireFormData['type']) => setForm((f) => ({ ...f, type })), []);
  const setIsShared = useCallback((isShared: boolean) => setForm((f) => ({ ...f, isShared })), []);
  const setUseSections = useCallback((useSections: boolean) => setForm((f) => ({ ...f, useSections })), []);
  const setSettings = useCallback((settings: QuestionnaireFormData['settings']) => {
    setForm((f) => ({ ...f, settings: { ...settings } }));
  }, []);

  const addSection = useCallback(() => {
    setForm((f) => {
      const id = newClientSectionId();
      const next: Section = { type: 'section', id, name: '', description: '', questions: [] };
      return { ...f, sections: [...f.sections, next] };
    });
  }, []);

  const removeSection = useCallback((sectionId: string) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.filter((s) => s.id !== sectionId),
    }));
  }, []);

  const updateSection = useCallback((sectionId: string, updates: Partial<Section>) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s) => (s.id === sectionId ? { ...s, ...updates } : s)),
    }));
  }, []);

  const addQuestion = useCallback((question: Question) => {
    setForm((f) => {
      const id = question.id || newClientQuestionId();
      const q = { ...question, id };
      return { ...f, questions: [...f.questions, q] };
    });
  }, []);

  const removeQuestion = useCallback((questionId: string) => {
    setForm((f) => ({
      ...f,
      questions: f.questions.filter((q) => q.id !== questionId),
    }));
  }, []);

  const addQuestionToSection = useCallback((sectionId: string, question: Question) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s) => {
        if (s.id !== sectionId) return s;
        const id = question.id || newClientQuestionId();
        return { ...s, questions: [...s.questions, { ...question, id }] };
      }),
    }));
  }, []);

  const removeQuestionFromSection = useCallback((sectionId: string, questionId: string) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s) => {
        if (s.id !== sectionId) return s;
        return { ...s, questions: s.questions.filter((q) => q.id !== questionId) };
      }),
    }));
  }, []);

  const updateQuestion = useCallback((questionId: string, updates: Partial<Question>, sectionId?: string) => {
    const cleaned: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(updates)) {
      if (!key?.trim()) continue;
      if (key === 'score' || key === 'minSelections' || key === 'maxSelections') {
        if (value !== undefined) cleaned[key] = value;
      } else if (value !== undefined) {
        cleaned[key] = value;
      }
    }
    setForm((f) => {
      if (sectionId) {
        return {
          ...f,
          sections: f.sections.map((s) => {
            if (s.id !== sectionId) return s;
            return {
              ...s,
              questions: s.questions.map((q) => (q.id === questionId ? ({ ...q, ...cleaned } as Question) : q)),
            };
          }),
        };
      }
      return {
        ...f,
        questions: f.questions.map((q) => (q.id === questionId ? ({ ...q, ...cleaned } as Question) : q)),
      };
    });
  }, []);

  const reset = useCallback(() => {
    structureBaselineRef.current = null;
    setForm(createEmptyForm());
  }, []);

  const loadFromQuestionnaire = useCallback(
    (payload: {
      title?: string;
      description?: string | null;
      type?: string;
      isShared?: boolean;
      schemaJson?: Record<string, unknown> | null;
    }) => {
      const rawType = payload.type;
      const normalizedType =
        rawType === 'poll'
          ? 'vote'
          : rawType === 'survey' ||
              rawType === 'evaluation' ||
              rawType === 'quiz' ||
              rawType === 'exam' ||
              rawType === 'assessment' ||
              rawType === 'vote'
            ? rawType
            : 'survey';

      if (payload.schemaJson && typeof payload.schemaJson === 'object') {
        const fd = schemaToFormData(
          payload.schemaJson as unknown as QuestionnaireSchema,
          payload.title ?? '',
          payload.description ?? undefined,
          normalizedType as QuestionnaireFormData['type']
        );
        const next = {
          ...fd,
          isShared: payload.isShared ?? fd.isShared,
        };
        setForm(next);
        structureBaselineRef.current = cloneFormBaseline(next);
        return;
      }

      const empty = {
        ...createEmptyForm(),
        title: payload.title ?? '',
        description: payload.description ?? undefined,
        type: normalizedType as QuestionnaireFormData['type'],
        isShared: payload.isShared ?? false,
        settings: createDefaultSettings(),
      };
      setForm(empty);
      structureBaselineRef.current = cloneFormBaseline(empty);
    },
    []
  );

  const getFormData = useCallback((): QuestionnaireFormData => ({ ...form, settings: { ...form.settings } }), [form]);

  const getStructureBaseline = useCallback(() => structureBaselineRef.current, []);

  return {
    form,
    setForm,
    setTitle,
    setDescription,
    setType,
    setIsShared,
    setUseSections,
    setSettings,
    addSection,
    removeSection,
    updateSection,
    addQuestion,
    removeQuestion,
    addQuestionToSection,
    removeQuestionFromSection,
    updateQuestion,
    reset,
    loadFromQuestionnaire,
    getFormData,
    getStructureBaseline,
  };
}
